import type { Currency } from "@/features/currencies/types";

/**
 * THE TICKER'S CURRENCIES — one row per token, not per (token, network). `/currency` lists a token once for every
 * network it lives on (USDT on Ethereum, Polygon and BSC), and the price is the token's, the same on each — so a
 * symbol is shown once, in the order it first appears. A wrapped token (WETH, WPOL) is the same price as the coin it
 * wraps and is left out when that coin is listed. The mark is the token's own image, never its network's.
 */
export function tickerRows(currencies: Currency[]): Currency[] {
  const priced = currencies.filter((c) => Number(c.price_sell_irr) > 0 && String(c.symbol ?? "").trim());
  const symbols = new Set(priced.map((c) => c.symbol.trim().toLowerCase()));
  const seen = new Set<string>();
  const rows: Currency[] = [];
  for (const c of priced) {
    const sym = c.symbol.trim().toLowerCase();
    if (seen.has(sym)) continue;
    if (sym.length > 1 && sym.startsWith("w") && symbols.has(sym.slice(1))) continue;
    seen.add(sym);
    rows.push(c);
  }
  return rows;
}

/** At least this many items in each half of the loop, so a half is wider than the widest screen. */
const PER_HALF = 18;
/** Seconds each item takes to pass, so the speed is the same however many currencies there are. */
const SECONDS_PER_ITEM = 4.6;

/** How many copies of the rows the loop draws (even: the two halves must be identical) and how long one half takes. */
export function tickerLoop(count: number): { copies: number; seconds: number } {
  if (count <= 0) return { copies: 0, seconds: 0 };
  const perHalf = Math.max(1, Math.ceil(PER_HALF / count));
  return { copies: perHalf * 2, seconds: Math.round(perHalf * count * SECONDS_PER_ITEM) };
}
